import { PERMISSIONS as P, PermissionName } from './permission.service';

/**
 * What a user needs to open each page or tab: any one of the listed view
 * permissions. The sidebar, the route guards and the tab bars all read this,
 * so a page is never shown to someone the API would refuse.
 * Pages missing here only show reference data every signed-in user may read.
 */
export const PAGE_ACCESS: Record<string, readonly PermissionName[]> = {
  dashboard: [P.TransactionView],
  analytics: [P.ReportView],
  transactions: [P.TransactionView],
  'transaction-report': [P.ReportView],
  'fee-report': [P.FeeReportView],
  reconciliation: [P.ReconciliationView],
  settlements: [P.SettlementView],
  payments: [P.PaymentView],
  transfers: [P.PaymentView],
  bills: [P.BillView],
  'bill-report': [P.ReportView],
  'wallet-management': [P.WalletView, P.ProfileView],
  'merchant-management': [P.MerchantView],
  'merchant-details': [P.MerchantView],
  'kyc-reviews': [P.KycView],
  applications: [P.ApplicationView],
  'user-management': [P.RoleView, P.StaffView, P.AuditView],
  configurations: [P.ScopeView, P.FeeView, P.CredentialView, P.MobileCredentialView, P.WebhookView,
    P.ApprovalLevelView, P.ReportView, P.SmsProviderView, P.SmsTemplateView],
  'credential-permissions': [P.CredentialView],
  fees: [P.FeeView],
  scopes: [P.ScopeView],
};

/** Tabs inside the configuration and merchant details pages. */
export const TAB_ACCESS: Record<string, readonly PermissionName[]> = {
  scopes: [P.ScopeView],
  fees: [P.FeeView],
  credentials: [P.CredentialView],
  'mobile-credentials': [P.MobileCredentialView],
  webhooks: [P.WebhookView],
  'approval-levels': [P.ApprovalLevelView],
  'report-columns': [P.ReportView],
  notifications: [P.NotificationView],
  users: [P.StaffView],
  wallets: [P.WalletView],
  'wallet-alerts': [P.WalletView],
  'wallet-sweeps': [P.WalletView],
  transactions: [P.TransactionView],
  payments: [P.PaymentView],
  transfers: [P.PaymentView],
  bills: [P.BillView],
  'bill-report': [P.ReportView],
  'transaction-report': [P.ReportView],
  'kyc-reviews': [P.KycView],
  applications: [P.ApplicationView],
  'credential-permissions': [P.CredentialView],
  sms: [P.SmsProviderView, P.SmsTemplateView],
};

/** Where a user lands after signing in: the first page they may open, in sidebar order. */
export const LANDING_ORDER = ['dashboard', 'transactions', 'payments', 'transfers', 'bills', 'wallet-management',
  'merchant-management', 'kyc-reviews', 'applications', 'reconciliation', 'settlements', 'analytics', 'transaction-report',
  'fee-report', 'bill-report', 'user-management', 'configurations', 'fees', 'scopes', 'credential-permissions'];

/** A page every signed-in user can open, for someone whose roles allow nothing else. */
export const FALLBACK_PAGE = 'settings';

export function landingPage(canOpen: (permissions: readonly PermissionName[]) => boolean): string {
  return LANDING_ORDER.find(page => canOpen(PAGE_ACCESS[page] ?? [])) ?? FALLBACK_PAGE;
}
